import { colors } from '@skyslope/mache';
import { all, call, put, takeEvery, select } from 'redux-saga/effects';
import type { IRootState } from '../..';
import type { IRecipient } from '../../../common/recipients/types';
import type { IFile } from '../../../dtm/types';
import { envelopeApi, recipientsApi } from '../../../lib/api/envelope';
import { primeApi } from '../../../lib/api/prime';
import { getBestRecipientMatchForOptionalRecipient, getRecipientColor, sortRecipients } from '../../../lib/utils';
import { errorToast, successToast } from '../../pageFrame/actions';
import * as pageFrameActions from '../../pageFrame/actions';
import type { IEnvelopeMetadata } from '../envelopes/types';
import * as actions from './actions';

const ActionTypes = actions.ActionTypes;

// @ts-expect-error -- This code was written prior to enforcing lint rules
export function* handleFetch(action: ReturnType<typeof actions.fetch>) {
  try {
    const metadata: IEnvelopeMetadata = yield call(envelopeApi.getEnvelopeMetadata, action.payload.envelopeId);
    yield put(pageFrameActions.updateRecipientColorCounter(metadata.recipientColorCounter));
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const file: IFile = yield call(primeApi.getFile, metadata.fileType, metadata.listingGuid ?? metadata.saleGuid);
    const [optionalPrimeRecipients, recipients] = yield all([
      call(
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        primeApi.getContacts,
        metadata.fileType,
        metadata.listingGuid ?? metadata.saleGuid,
        action.payload.impersonatedPrimeUserGuid
      ),
      call(recipientsApi.getRecipients, action.payload.envelopeId, true),
    ]);

    // Assign color to recipients

    recipients.forEach((r: IRecipient, _i: number) => {
      r.color = getRecipientColor(r.recipientColorCounter, r.title);
    });

    // optional =  file contacts from dtm
    // recipients = expected recipients from the applied template and/or added recipients
    const mappedRecipientIds: string[] = [];
    // Track how many recipients with each title have been matched to preserve order
    const titleMatchCounts: { [title: string]: number } = {};
    // get the optional placeholders from recipients

    optionalPrimeRecipients.forEach((or: IRecipient, i: number) => {
      const titleLower = or.title?.toLowerCase() || '';
      // How many recipients with this title have been matched previously
      const titleMatchIndex = titleMatchCounts[titleLower] ?? 0;
      const recipient = getBestRecipientMatchForOptionalRecipient(mappedRecipientIds, recipients, or, titleMatchIndex);
      const index = recipients.indexOf(recipient);

      // if recipient title matches with a optionalRecipient title
      if (index >= 0) {
        if (!recipients[index].email) {
          // First time after appplying a template
          or.id = recipients[index].id;
          or.color = recipients[index].color;
          or.group = { id: 'default', name: 'default', order: 0 };
          or.isActive = recipients[index].isActive || !!(or.firstName && or.email);
          or.isPlaceholder = true;
          or.recipientColorCounter = recipients[index].recipientColorCounter;
          recipients[index] = { ...or };
        } else {
          if (recipients[index].email === null) {
            recipients[index].email = '';
          }
          if (recipients[index].firstName === null) {
            recipients[index].firstName = '';
          }
          optionalPrimeRecipients[i] = { ...recipients[index] };
        }
        // recipients id already taken
        mappedRecipientIds.push(recipients[index].id);
        // Increment the match count for this title to preserve order for next match
        titleMatchCounts[titleLower] = (titleMatchCounts[titleLower] ?? 0) + 1;
      } else {
        or.isActive = false;
        or.isPlaceholder = false;
        or.color = colors.grey[600];
        optionalPrimeRecipients[i] = or;
      }
    });
    sortRecipients(recipients);
    yield put(actions.fetchDone(file, optionalPrimeRecipients, recipients));
  } catch {
    yield put(errorToast('Failed to fetch recipients, please refresh!'));
  }
}

export function* handleSave(action: ReturnType<typeof actions.save>) {
  try {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const recipients = yield select((state: IRootState) => state.dtm.recipients.recipients);
    yield call(recipientsApi.updateRecipients, recipients, action.payload);
    yield put(successToast('Recipient changes saved.'));
    yield put(actions.saveDone());
  } catch {
    yield put(errorToast('Failed to save recipients'));
  }
}

export function* handleFetchContacts(_action: ReturnType<typeof actions.fetchContacts>) {
  try {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const contacts: any = yield call(primeApi.getContactsForPrimeUser);
    yield put(actions.fetchContactsDone(contacts));
  } catch {
    console.info('Error fetching contacts');
  }
}

export function* handleCreatePrimeContact(action: ReturnType<typeof actions.createPrimeContact>) {
  try {
    yield call(primeApi.createPrimeContact, action.payload);
  } catch {
    console.info('Error saving contact to prime');
  }
}

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default function* rootSaga() {
  yield all([takeEvery(ActionTypes.FETCH, handleFetch), takeEvery(ActionTypes.SAVE, handleSave)]);
  yield all([takeEvery(ActionTypes.FETCH_CONTACTS, handleFetchContacts)]);
  yield all([takeEvery(ActionTypes.CREATE_PRIME_CONTACT, handleCreatePrimeContact)]);
}
